import {
  cartWidget,
  resetAdditionalItems,
  resetBattery,
  resetDeliveryOption,
  resetInternet,
  resetPhone,
  resetPoints,
  resetShippingAddressState,
  resetStaticIp,
  setServiceAddress,
  useCartState,
} from '@/app/signup/(state)/CartStore';
import {useGlobalAddressContext} from '@/app/signup/(state)/GlobalAddressContext';
import {setHeader} from '@/app/signup/(state)/HeaderStore';
import {resetNotificationPreferencesState} from '@/app/signup/(state)/NotificationPreferencesStore';
import {paymentWidget} from '@/app/signup/(state)/PaymentStore';
import {resetPhoneQuestionnaireState} from '@/app/signup/(state)/PhoneQuestionnaireStore';
import {resetPreconfigState} from '@/app/signup/(state)/PreconfigStore';
import {resetScheduleState} from '@/app/signup/(state)/ScheduleStore';
import {resetStreamingState} from '@/app/signup/(state)/StreamingStore';
import {resetVerifyState} from '@/app/signup/(state)/VerifyStore';
import {resetWifiSetupState} from '@/app/signup/(state)/WifiSetupStore';
import {useAnalytics} from '@/hooks/useAnalytics';
import type {
  BuyflowWidget,
  GetNextBuyflowStepRequest,
  GetNextBuyflowStepResponse,
  ProductDeliveryChoice,
  ProductWidget,
} from '@/lib/TypesOpenApi';
import {
  buyflowRequestExeptionTracking,
  useAnalyticsNavButton,
} from '@/lib/analytics/helper';
import {useRouter, useSearchParams} from 'next/navigation';
import {useCallback, useState} from 'react';
import {clone} from '../../../../lib/utils/clone';
import {
  addToHistory,
  store as historyStore,
  removePagesFromHistory,
  useLocalHistory,
} from '../LocalHistoryState';
import {
  type Step,
  type StepsPath,
  getPrevIndexAndSteps,
  stepsList,
  stepsMap,
} from '../StepsRouting';
import {useCachedFetchByflowStepData} from '../api/useFetchData';
import {setBuyflowState} from '../buyflowStore';
import {mapState2Api} from '../mapState2Api';
import {setWaitWidget} from '../waitMessageStore';
import {syncSelectedProducts} from './syncSelectedProducts';
import {
  buildSignupUrl,
  buyflowEnded,
  useRedirectForEndBuyflow,
} from './useSignupRedirect';

const dummyFunc = () => {};

export const useGoToNextStep = (currentStep: Step) => {
  const loadStep = useLoadStep(currentStep, false);

  return useCallback(
    /**
     * @param onErrror - handler provide ability to handle exception.
     *  Not a best practice, but majority of codebase not ready to catch exception directly
     * */
    async (step?: string, onError?: (error: any) => void) => {
      return await loadStep(step, onError);
    },
    [loadStep],
  );
}; // todo: need keep use callback to keep button loader

export const useLoadCurrentStep = (currentStep: Step) =>
  useLoadStep(currentStep, true); // TODO: need use https://github.com/vigzmv/react-promise-suspense to suspense current component

/* If refresh true will load current step data, if false will load next step */
export const useLoadStep = (currentStep: Step, refresh: boolean) => {
  const goTo = useGoToWithPreserveParams();
  const redirectTo = useRedirectForEndBuyflow();
  const {globalAddressId} = useGlobalAddressContext();
  const loadStepWidget = useLoadStepWidget();
  const isBasic = getBasicParamValue(useSearchParams());
  const [isFirstRequestDone, setIsFirstRequestDone] = useState(false);
  const analytics = useAnalytics();

  return useCallback(
    async (step?: string, onError: (error: any) => void = dummyFunc) => {
      if (buyflowEnded.hasBuyflowEnded) {
        return;
      }

      // collect app state and map it to API format
      if (step === 'BILLING') {
        currentStep.name = step;
        currentStep.store = paymentWidget;
      }

      let isLoading = true;
      /*
       * We need to set the message here since we only want to display the message
       * right before go to next step, but not during the loading of the step.
       *
       * Both the drawer and waiting widget uses modal dialog, which
       * sets "overflow: hidden" on html body.  Need to wait for any open
       * drawer to close before starting the waiting widget, to avoid
       * the overflow property being left in a wrong state that prevents
       * billing dialog vertical scrolling.
       */
      setTimeout(() => {
        if (isLoading) {
          setWaitWidget(currentStep.store.content?.buyflowStep);
        }
      }, 500);

      const payload = mapState2Api(globalAddressId!, {
        currentStep,
        refresh,
        isBasic,
      });
      try {
        // load step widget data and prefil it for next page
        const {nextStep} = await loadStepWidget(
          globalAddressId!,
          clone(payload),
        );

        const buyflowState = nextStep.store.content?.buyflowState;
        const firstWidget =
          nextStep.store.content?.buyflowStep.buyflowWidgets[0];
        analytics.setBuyflowType(
          buyflowState?.buyflowType || 'BUY_FLOW_TYPE_UNSPECIFIED',
        );
        analytics.setStepName(nextStep.path);
        analytics.setAppNameField();
        analytics.setAddressEquipmentState(
          buyflowState?.serviceAddressInfo?.addressEquipmentState,
        );

        analytics.setMarketInfo(buyflowState?.serviceAddressInfo?.marketInfo);
        // TODO: need BFF to send addressType
        analytics.setAddressType(
          buyflowState?.serviceAddressInfo?.primaryAddressType || '',
          buyflowState?.serviceAddressInfo?.secondaryAddressType || '',
        );
        // TODO: need BFF to send plans in the first request
        analytics.setInternetPlans(
          firstWidget?.internetProductWidget?.productWidgets.map(
            (widget: ProductWidget) =>
              widget.productName || widget.productId || '',
          ) || [],
        );
        // TODO: set these fields once BFF sends them.
        analytics.setField('addressDescription', 'NOT_IMPLEMENTED');
        analytics.setDaysEligible(
          buyflowState?.serviceAddressInfo?.eligibilityStartDateSeconds || 0,
        );

        const experiments = buyflowState?.experiments;
        if (!isFirstRequestDone && experiments && experiments.length > 0) {
          setIsFirstRequestDone(true);
          analytics.trackExperementEvents(experiments);
        }
        analytics.trackCheckEligible(
          buyflowState?.serviceAddressInfo?.physicalAddress?.zipcode || '',
        );

        if (nextStep.name === 'END_BUYFLOW') {
          const redirectUrl = firstWidget?.redirectWidget?.redirectURL || '/';
          redirectTo(redirectUrl);
          buyflowEnded.hasBuyflowEnded = true;
          return;
        }
        // even if loading current step data,
        // back-end can redirect us to another page
        if (currentStep.name !== nextStep.name) {
          goTo(nextStep.path);
          return;
        }
      } catch (error) {
        console.error('Error during loading step widget', error);
        setWaitWidget(undefined);
        onError(error);
        buyflowRequestExeptionTracking(
          currentStep.name,
          currentStep?.store.content?.buyflowState.buyflowType,
          error,
        );
        return;
      } finally {
        isLoading = false;
      }
    },
    [
      currentStep,
      globalAddressId,
      refresh,
      isBasic,
      loadStepWidget,
      isFirstRequestDone,
      analytics,
      redirectTo,
      goTo,
    ],
  );
};

/** Usefull to load next or current step data */
export const useLoadStepWidget = () => {
  const fetchBuyflowStep = useCachedFetchByflowStepData();

  return useCallback(
    async (globalAddressId: string, payload: GetNextBuyflowStepRequest) => {
      // request next step data
      const widget = await fetchBuyflowStep(globalAddressId!, payload);

      // prefil store of desired page before going into it
      return {
        widget,
        ...saveStepWidget(widget, payload),
      };
    },
    [fetchBuyflowStep],
  );
};

const findAndSaveCartWidget = (widget: GetNextBuyflowStepResponse) => {
  widget.buyflowStep.buyflowWidgets.forEach((w: BuyflowWidget) => {
    Object.values(w).forEach((v: any) => {
      if (v?.cartWidget) {
        cartWidget.widget = v.cartWidget;
      }
    });
  });
};

export const getBasicParamValue = (currentParams: URLSearchParams): boolean => {
  const basicParamValue = currentParams.get('basic');
  return !!(basicParamValue && basicParamValue.toLowerCase() === 'true');
};

/** prefil store of desired page before going into it */
export const saveStepWidget = (
  widget: GetNextBuyflowStepResponse,
  oldState: GetNextBuyflowStepRequest,
) => {
  const nextStepName = widget.buyflowState.currentStep;
  const nextStep = stepsMap[nextStepName];
  if (!nextStep) {
    throw new Error(`Received unkown step: ${nextStepName}`);
  }
  findAndSaveCartWidget(widget);
  nextStep.store.content = widget;

  /*
   * We need to clear the message here since we only want to display the message
   * right before go to next step, but not during the loading of the step.
   */
  setWaitWidget(undefined);

  // save to persist meta data between sessions
  setBuyflowState(widget.buyflowState);

  setHeader(widget.buyflowStep);

  syncSelectedProducts(oldState, widget);

  setServiceAddress(widget?.buyflowState.serviceAddressInfo?.physicalAddress);

  return {
    nextStepName,
    nextStep,
  };
};

export const useGoToPreviousStep = (currentStep: Step) => {
  const router = useRouter();
  const {pages} = useLocalHistory();
  const analyticsBackButton = useAnalyticsNavButton('back');
  const {
    deliveryOption: {productDeliveryChoice},
  } = useCartState();

  return useCallback(() => {
    /*
     * This should be based on the page/step name.
     * There might be a use case which we don't want to the user go back on certain step
     * which are not one of the very first several steps.
     * Index should start from 1 since we always add the first step in the history.
     * Our first step /signup will always call nextStep automatically
     * go to the really first step, which will varies depend on BFF return.
     */

    const [currentPageIndex, stepsBack] = getPrevIndexAndSteps(
      currentStep.path as StepsPath,
      pages,
    );
    // Step back is prohibited
    if (currentPageIndex === -1 && stepsBack === 0) {
      return;
    }

    analyticsBackButton(currentStep);
    // Remove the pages which are after current step since they are not valid anymore.
    removePagesFromHistory(currentPageIndex as number);
    // Clear the states of those invalid steps which were removed from above.
    clearStoredDataForInvaidSteps(productDeliveryChoice);
    for (
      let i = 0;
      window.history?.length && window.history.length > 1 && i < stepsBack;
      i++
    ) {
      router.back();
    }
    // need to remove next steps from store to avoid products widgets inconsistens
    stepsMap[currentStep.name].store.content = undefined;
  }, [analyticsBackButton, productDeliveryChoice, currentStep, pages, router]);
};

/**
 * Allow go to path while preserving search params
 * TODO: switch to dynamic routing to remove this hack
 * */
export const useGoToWithPreserveParams = () => {
  const router = useRouter();
  const searchParams = useSearchParams(); // TODO: switch to dynamic routing to remove this hack

  return useCallback(
    (path: string) => {
      const fullPath = buildSignupUrl(path, searchParams);

      // avoid router.push to keep steps history localy and avoid conflicts with browser history
      router.push(fullPath);
      addToHistory(path as StepsPath);
    },
    [router, searchParams],
  );
};

/**
 * Clean stored data for the visted steps which not valid any more.
 * Those steps are not valid any more is because
 * the user back to certain previous step either by clicking the back button
 * or by input the url of a previous step directly.
 */
export const clearStoredDataForInvaidSteps = (
  productDeliveryChoice: ProductDeliveryChoice,
) => {
  // Find if the steps if valid or not by comparing with the history.
  const pages = historyStore.pages;
  for (const step of stepsList) {
    if (!pages.includes(step.path as StepsPath)) {
      // Clean the widget defined for each step.
      step.store.content = undefined;
      /*
       * In addition to clean the widget defined for each step,
       * clean other states defined for specific steps.
       */
      switch (step.name) {
        /*
         * Preconfig states should not need to reset but add it here in case
         * thing changes that preconfig will not be the first step anymore.
         */
        case 'PRECONFIG':
          resetPreconfigState();
          break;
        case 'INTERNET':
          resetInternet();
          // TODO: double check should reset additional items on this step.
          resetAdditionalItems();
          // TODO: double check should all points items be reset on this step.
          // Points page and router equipment selected from internet page used the same data structure.
          resetPoints();
          break;
        case 'PHONE':
          resetPhone();
          break;
        case 'PHONE_QUESTIONNAIRE':
          resetPhoneQuestionnaireState();
          break;
        case 'BATTERY':
          resetBattery();
          break;
        case 'STATIC_IP':
          resetStaticIp();
          break;
        case 'POINT_SELECTOR':
          break;
        case 'STREAMING':
          resetStreamingState();
          break;
        case 'SELF_INSTALL_KIT':
          resetShippingAddressState();
          if (productDeliveryChoice !== 'PRO_INSTALL') {
            resetDeliveryOption();
          }
          break;
        case 'SCHEDULING':
          if (productDeliveryChoice === 'PRO_INSTALL') {
            resetDeliveryOption();
            resetScheduleState();
          }
          break;
        case 'VERIFY':
          resetVerifyState();
          break;
        case 'WIFI_SETUP':
          resetWifiSetupState();
          break;
        case 'NOTIFICATION_PREFERENCES':
          resetNotificationPreferencesState();
          break;

        default:
      }
    }
  }
};
